import React, { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { CalendarDays, Receipt, Package, Repeat, Target, Phone, Mail, Loader2, Activity, Download, LineChart, X } from "lucide-react";
import { api } from "../lib/api";
import TimeGrid from "./TimeGrid";
import {
  BOOKING_WINDOW,
  fmtTimeWords,
  isOutsideWindow,
} from "../lib/bookingWindow";
import { reliabilityStyle, formatReliabilityScore, reliabilityInsight } from "../lib/reliability";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "./ui/alert-dialog";
import ProgressTab from "./ProgressTab";
import { getBilling } from "../lib/billing";
import { BillingChip } from "./BillingChip";
import SessionDetailsDialog from "./SessionDetailsDialog";
import { PackCoverageDialog } from "./PackCoverageDialog";

const fmtAud = (n) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n || 0);
const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", { timeZone: "Australia/Melbourne", day: "numeric", month: "short", year: "numeric" })
    : "—";
const fmtDueShort = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", {
        timeZone: "Australia/Melbourne",
        day: "numeric",
        month: "short",
      })
    : "";
const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

function invoiceBadge(status) {
  switch (status) {
    case "paid":
      return "bg-[#0F6B6B]/15 text-[#0F6B6B]";
    case "overdue":
      return "bg-[#F9E4DF] text-[#A8372A]";
    case "sent":
      return "bg-[#E1EDF7] text-[#1F5C8F]";
    default:
      return "bg-[#EEE9E0] text-[#6B635C]";
  }
}

function sessionBadge(status) {
  switch (status) {
    case "completed":
      return "bg-[#0F6B6B]/15 text-[#0F6B6B]";
    case "no-show":
      return "bg-[#F9E4DF] text-[#A8372A]";
    case "cancelled":
      return "bg-[#EEE9E0] text-[#6B635C]";
    default:
      return "bg-[#E1EDF7] text-[#1F5C8F]";
  }
}

const tabs = [
  { key: "sessions", label: "Sessions", icon: CalendarDays },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "packages", label: "Packages", icon: Package },
  { key: "progress", label: "Progress", icon: LineChart },
];

function renderHistoryTab({ loading, tab, data, client, onReload, onSelectSession, billingCtx }) {
  if (tab === "progress") return <ProgressTab client={client} />;
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#6B635C] py-12 justify-center" data-testid="history-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
      </div>
    );
  }
  if (tab === "sessions") return <SessionsList sessions={data?.sessions || []} onSelectSession={onSelectSession} billingCtx={billingCtx} />;
  if (tab === "invoices") return (
    <InvoicesTabView
      invoices={data?.invoices || []}
      series={data?.recurring || []}
      packages={data?.packages || []}
      client={client}
      onReload={onReload}
    />
  );
  return <PackagesList packages={data?.packages || []} />;
}
export default function ClientHistoryDrawer({ client, open, onOpenChange }) {
  const [tab, setTab] = useState("sessions");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const reload = useCallback(async () => {
    if (!client) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/clients/${client.client_id}/history`);
      setData(res);
    } catch {
      setData({ sessions: [], invoices: [], packages: [], recurring: [] });
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => {
    if (!open || !client) {
      setData(null);
      setSelectedSession(null);
      return;
    }
    reload();
  }, [open, client, reload]);

  useEffect(() => {
    if (open) setTab("sessions");
  }, [open, client?.client_id]);

  if (!client) return null;

  const counts = data
    ? {
        sessions: data.sessions.length,
        invoices: data.invoices.length,
        packages: data.packages.length,
      }
    : { sessions: 0, invoices: 0, packages: 0 };

  const paidRevenue = (data?.invoices || [])
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + (i.total || 0), 0);
  const completedSessions = (data?.sessions || []).filter((s) => s.status === "completed").length;
  const invoiceBySession = Object.fromEntries(
    (data?.invoices || []).filter((i) => i.session_id).map((i) => [i.session_id, i]),
  );
  const packById = Object.fromEntries((data?.packages || []).map((p) => [p.package_id, p]));
  const invoiceByPackage = Object.fromEntries(
    (data?.invoices || []).filter((i) => i.package_id).map((i) => [i.package_id, i]),
  );
  const billingCtx = { invoiceBySession, packById, invoiceByPackage };
  const reliability = client.reliability;
  const relStyle = reliabilityStyle(reliability?.tier);
  // Cancellation + reschedule counts sit under the reliability card. Read
  // them from the freshly-fetched history `data` (which includes both blocks)
  // so they refresh the instant any drawer action calls reload(); fall back to
  // the roster snapshot on the very first render before data arrives.
  const cancellations = data?.cancellations ?? client.cancellations;
  const reschedules = data?.reschedules ?? client.reschedules;
  const cancelShown =
    (cancellations?.by_client ?? 0) +
    (cancellations?.by_trainer ?? 0) +
    (cancellations?.from_series ?? 0);
  const reschedShown =
    (reschedules?.by_client ?? 0) +
    (reschedules?.by_trainer ?? 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        hideClose
        className="bg-[#FAF7F2] border-l border-[#E8E1D6] text-[#2A2623] w-full sm:max-w-lg p-0 flex flex-col overflow-hidden"
        data-testid="client-history-drawer"
      >
        {/* Fixed header bar: the close button never scrolls away and keeps a
            44x44 touch area on a phone. */}
        <div className="shrink-0 flex items-center justify-between gap-2 pl-4 pr-1 h-14 border-b border-[#E8E1D6] bg-[#FAF7F2]">
          <p className="tm-label truncate" data-testid="drawer-bar-name">{client.name}</p>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close client history"
            data-testid="drawer-close"
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-md text-[#2A2623] hover:text-[#2A2623] hover:bg-[#E8E1D6] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="p-6 border-b border-[#E8E1D6]">
          <SheetHeader className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full bg-[#E8E1D6] flex items-center justify-center text-base font-bold text-[#0F6B6B]"
                aria-hidden="true"
              >
                {client.name
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="tm-label">Client History</p>
                <SheetTitle className="font-display text-2xl uppercase text-[#2A2623] truncate">
                  {client.name}
                </SheetTitle>
              </div>
            </div>
            <SheetDescription className="text-[#6B635C] text-xs">
              Past sessions, invoices and packages for this client.
            </SheetDescription>

            <div className="flex flex-col gap-1 pt-2 text-sm text-[#2A2623]">
              {client.goal && (
                <span className="flex items-center gap-2 text-[#0F6B6B]">
                  <Target className="w-3.5 h-3.5" /> {client.goal}
                </span>
              )}
              {client.phone && (
                <span className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#6B635C]" /> {client.phone}
                </span>
              )}
              {client.email && (
                <span className="flex items-center gap-2 truncate">
                  <Mail className="w-3.5 h-3.5 text-[#6B635C]" /> {client.email}
                </span>
              )}
            </div>
          </SheetHeader>

          <div className="grid grid-cols-2 gap-3 mt-5" data-testid="history-summary">
            <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3">
              <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Completed</p>
              <p className="font-display text-2xl font-bold mt-1">{completedSessions}</p>
              <p className="text-[10px] text-[#6B635C]">sessions</p>
            </div>
            <div className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3">
              <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Paid revenue</p>
              <p className="font-display text-2xl font-bold mt-1 text-[#0F6B6B]">{fmtAud(paidRevenue)}</p>
              <p className="text-[10px] text-[#6B635C]">GST incl.</p>
            </div>
          </div>

          <div
            className={`mt-3 rounded-md border ${relStyle.chip} bg-[#FFFFFF]/60 p-3`}
            data-testid="history-reliability"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Activity className={`w-3.5 h-3.5 ${relStyle.text}`} />
                <span className="text-[10px] uppercase tracking-widest text-[#2A2623]">
                  Reliability · last {reliability?.window_days ?? 90} days
                </span>
              </div>
              <span
                data-testid="history-reliability-tier"
                className={`text-[10px] uppercase tracking-widest font-bold ${relStyle.text}`}
              >
                {relStyle.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2 mt-1.5">
              <span
                data-testid="history-reliability-score"
                className={`font-display text-3xl font-black ${relStyle.text}`}
              >
                {formatReliabilityScore(reliability)}
              </span>
              <span className="text-xs text-[#6B635C]">
                {reliability?.completed ?? 0} completed · {reliability?.no_shows ?? 0} no-show
                {(reliability?.no_shows ?? 0) === 1 ? "" : "s"} · {reliability?.sample ?? 0} total
              </span>
            </div>
            <p className="text-[11px] text-[#6B635C] mt-1.5 leading-snug">
              {reliabilityInsight(reliability)}
            </p>
          </div>

          {cancelShown > 0 && (
            <p
              className="mt-2 text-[11px] text-[#6B635C] leading-snug"
              data-testid="history-cancellations"
            >
              <span className="uppercase tracking-widest text-[10px] text-[#6B635C]">
                Cancelled (last {cancellations?.window_days ?? 90} days):
              </span>{" "}
              <span className="text-[#7A5E18]">
                {cancellations.by_client} by client
                {cancellations.by_client_late > 0 ? ` (${cancellations.by_client_late} late)` : ""}
              </span>
              {" · "}
              <span className="text-[#1F5C8F]">{cancellations.by_trainer} by you</span>
              {" · "}
              <span className="text-[#2A2623]">{cancellations.from_series} from series</span>
            </p>
          )}

          {reschedShown > 0 && (
            <p
              className="mt-1 text-[11px] text-[#6B635C] leading-snug flex flex-wrap items-baseline gap-x-1 gap-y-0.5"
              data-testid="history-reschedules"
            >
              <span className="uppercase tracking-widest text-[10px] text-[#6B635C]">
                Rescheduled (last {reschedules?.window_days ?? 90} days):
              </span>{" "}
              <span className="text-[#7A5E18]">
                {reschedules.by_client} by client
                {reschedules.by_client_late > 0 ? ` (${reschedules.by_client_late} late)` : ""}
              </span>
              <span className="text-[#6B635C]">·</span>
              <span className="text-[#1F5C8F]">{reschedules.by_trainer} by you</span>
            </p>
          )}
        </div>

        <div className="px-6 pt-4">
          <div className="flex gap-0.5 sm:gap-1 border-b border-[#E8E1D6]" role="tablist">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`history-tab-${t.key}`}
                  role="tab"
                  aria-selected={active}
                  className={`flex-1 min-w-0 flex items-center justify-center sm:justify-start gap-1 sm:gap-2 px-1 sm:px-3 py-2 text-[10px] sm:text-xs uppercase tracking-wide sm:tracking-widest font-semibold border-b-2 -mb-px transition-colors ${
                    active
                      ? "border-[#0F6B6B] text-[#0F6B6B]"
                      : "border-transparent text-[#6B635C] hover:text-[#2A2623]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 hidden sm:inline-block shrink-0" />
                  <span className="truncate">{t.label}</span>
                  {counts[t.key] !== undefined && (
                    <span className="text-[10px] text-[#6B635C] hidden sm:inline">({counts[t.key]})</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-6 py-5">
          {renderHistoryTab({ loading, tab, data, client, onReload: reload, onSelectSession: setSelectedSession, billingCtx })}
        </div>
        </div>
      </SheetContent>

      <SessionDetailsDialog
        session={selectedSession}
        onOpenChange={(o) => !o && setSelectedSession(null)}
        clientName={client.name}
        invoice={selectedSession ? invoiceBySession[selectedSession.session_id] : null}
        billing={selectedSession ? getBilling(selectedSession, billingCtx) : null}
        onChanged={reload}
      />
    </Sheet>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-12 text-center text-[#6B635C] text-sm" data-testid="history-empty">
      No {label} yet.
    </div>
  );
}

function SessionsList({ sessions, onSelectSession, billingCtx }) {
  if (!sessions.length) return <EmptyState label="sessions" />;
  return (
    <ul className="space-y-2" data-testid="history-sessions-list">
      {sessions.map((s) => {
        const billing = getBilling(s, billingCtx);
        return (
        <li
          key={s.session_id}
          role="button"
          tabIndex={0}
          onClick={() => onSelectSession?.(s)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectSession?.(s);
            }
          }}
          className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3 flex items-center gap-3 cursor-pointer hover:border-[#0F6B6B]/40 hover:bg-[#FFFFFF]/80 transition-colors"
          data-testid={`history-session-${s.session_id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{fmtDateTime(s.starts_at)}</div>
            <div className="text-xs text-[#6B635C] mt-0.5">
              {s.duration_min}min · {s.session_type}
            </div>
            {billing && !billing.packReturned && (
              <div className="mt-1">
                <BillingChip billing={billing} sessionId={s.session_id} />
              </div>
            )}
          </div>
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${sessionBadge(s.status)}`}
          >
            {s.status}
          </span>
        </li>
        );
      })}
    </ul>
  );
}

function InvoicesTabView({ invoices, series, packages, client, onReload }) {
  return (
    <div className="space-y-5" data-testid="history-invoices-tab">
      <RecurringSection series={series} invoices={invoices} packages={packages} client={client} onReload={onReload} />
      <div>
        <p className="text-[10px] uppercase tracking-widest text-[#6B635C] mb-2">Invoices</p>
        <InvoicesList invoices={invoices} />
      </div>
    </div>
  );
}

function InvoicesList({ invoices }) {
  if (!invoices.length) return <EmptyState label="invoices" />;
  const downloadPdf = async (inv, evt) => {
    try {
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }

      // Absolute URL via REACT_APP_BACKEND_URL (no relative paths)
      const base = process.env.REACT_APP_BACKEND_URL;
      const absUrl = `${base}/api/invoices/${inv.invoice_id}/pdf`;

      // Cookie-based session — include credentials so the auth cookie is sent.
      const res = await fetch(absUrl, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/pdf" },
      });

      if (!res.ok) {
        const detail = res.status === 401 ? "Session expired. Please log in again." : `Server returned ${res.status}`;
        toast.error(`PDF download failed. ${detail}`);
        return;
      }

      const blob = await res.blob();

      if (!blob.size) {
        toast.error("PDF download failed. Empty file returned.");
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}-TrainMate.pdf`;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      // Give the browser a tick to start the download before cleanup
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 0);
      toast.success(`Downloading ${inv.invoice_number}.pdf`);
    } catch {
      toast.error("PDF download failed. Please try again.");
    }
  };
  return (
    <ul className="space-y-2" data-testid="history-invoices-list">
      {invoices.map((i) => (
        <li
          key={i.invoice_id}
          className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3 flex items-center gap-3"
          data-testid={`history-invoice-${i.invoice_id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{i.invoice_number}</div>
            <div className="text-xs text-[#6B635C] mt-0.5">{fmtDate(i.created_at)}</div>
            <div className="text-[11px] mt-0.5" data-testid={`history-invoice-due-${i.invoice_id}`}>
              {i.due_date ? (
                <span className={i.status === "overdue" ? "text-[#A8372A]" : "text-[#6B635C]"}>
                  Due {fmtDueShort(i.due_date)}
                  {i.status === "overdue" && ` · Overdue ${i.overdue_days ?? 0} day${(i.overdue_days ?? 0) === 1 ? "" : "s"}`}
                </span>
              ) : (
                <span className="text-[#6B635C]">
                  {i.status === "draft" ? "Due date set when sent" : "No due date"}
                </span>
              )}
            </div>
            {i.awaiting_abn && i.status === "draft" && (
              <div
                data-testid={`history-invoice-awaiting-abn-${i.invoice_id}`}
                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#FBF4DD] text-[#7A5E18] border border-[#ECDCA6]"
              >
                Add your ABN to send
              </div>
            )}
            {i.recurring_series_id && (
              <div
                data-testid={`history-invoice-recurring-tag-${i.invoice_id}`}
                className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#0F6B6B]/10 text-[#0F6B6B] border border-[#0F6B6B]/25"
              >
                <Repeat className="w-3 h-3" /> Recurring · Week {i.series_week} of {i.series_total_weeks}
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="font-display font-bold">{fmtAud(i.total)}</div>
            <div
              className="text-[10px] text-[#6B635C]"
              data-testid={`history-invoice-gst-${i.invoice_id}`}
            >
              {i.gst_applied === false ? "No GST" : "GST incl."}
            </div>
            <span
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${invoiceBadge(i.status)}`}
            >
              {i.status}
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => downloadPdf(i, e)}
            data-testid={`history-invoice-pdf-${i.invoice_id}`}
            title="Download PDF"
            aria-label={`Download PDF for invoice ${i.invoice_number}`}
            className="h-8 w-8 rounded-md inline-flex items-center justify-center text-[#6B635C] hover:text-[#0F6B6B] hover:bg-[#FAF7F2]"
          >
            <Download className="w-4 h-4" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function PackagesList({ packages }) {
  if (!packages.length) return <EmptyState label="packages" />;
  return (
    <ul className="space-y-2" data-testid="history-packages-list">
      {packages.map((p) => {
        const total = p.total_sessions || 0;
        const remaining = p.sessions_remaining ?? total;
        return (
          <li
            key={p.package_id}
            className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-3"
            data-testid={`history-package-${p.package_id}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">{p.name || "Package"}</div>
              <div className="font-display font-bold text-[#0F6B6B]">
                {remaining}
                <span className="text-xs text-[#6B635C]"> / {total} left</span>
              </div>
            </div>
            <div className="text-xs text-[#6B635C] mt-1">
              Purchased {fmtDate(p.created_at)} · {fmtAud(p.price)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}


const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAY_FULL = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKDAY_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function statusChip(status) {
  const base = "text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded";
  if (status === "active") return `${base} bg-[#0F6B6B]/15 text-[#0F6B6B]`;
  if (status === "cancelled") return `${base} bg-[#EEE9E0] text-[#6B635C]`;
  if (status === "completed") return `${base} bg-[#E1EDF7] text-[#1F5C8F]`;
  return `${base} bg-[#EEE9E0] text-[#6B635C]`;
}

function RecurringSection({ series, invoices, packages, client, onReload }) {
  const [creating, setCreating] = useState(false);
  const credits = (packages || []).reduce(
    (sum, p) => sum + Math.max(0, Number(p.sessions_remaining) || 0),
    0
  );
  const active = series.filter((s) => s.status === "active");
  const past = series.filter((s) => s.status !== "active");
  return (
    <div className="space-y-3" data-testid="history-recurring-list">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Recurring series</p>
          <p className="text-xs text-[#6B635C] mt-0.5">
            Books every class now. Each class is invoiced when you mark it complete.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setCreating(true)}
          data-testid="recurring-new-btn"
          className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold text-xs"
        >
          <Repeat className="w-3.5 h-3.5" /> Set up recurring
        </Button>
      </div>

      {active.length === 0 && past.length === 0 && (
        <div
          data-testid="recurring-empty"
          className="rounded-md border border-dashed border-[#E8E1D6] bg-[#FFFFFF]/40 p-3 text-center text-xs text-[#6B635C]"
        >
          No recurring series yet. Books the weekly classes upfront — each one is invoiced when you mark it complete.
        </div>
      )}

      {active.map((s) => (
        <RecurringCard key={s.series_id} series={s} invoices={invoices} onReload={onReload} credits={credits} />
      ))}

      {past.length > 0 && (
        <details className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF]/40 p-2" data-testid="recurring-past-toggle">
          <summary className="text-[10px] uppercase tracking-widest text-[#6B635C] cursor-pointer px-1 py-0.5">
            Past series ({past.length})
          </summary>
          <div className="space-y-2 mt-2">
            {past.map((s) => (
              <RecurringCard key={s.series_id} series={s} invoices={invoices} onReload={onReload} />
            ))}
          </div>
        </details>
      )}

      <RecurringCreateDialog
        open={creating}
        onOpenChange={setCreating}
        client={client}
        credits={credits}
        onCreated={onReload}
      />
    </div>
  );
}

function buildTimeline(series, invoices) {
  // Preferred path: backend-reconciled per-week truth.
  const states = series.week_states;
  if (Array.isArray(states) && states.length) {
    return states.map((w) => {
      const inv =
        (invoices || []).find((i) => i.invoice_id === w.invoice_id) ||
        (w.invoice_id
          ? { invoice_number: w.invoice_number, status: w.invoice_status || "draft" }
          : null);
      const date = w.starts_at
        ? new Date(w.starts_at)
        : inv && inv.created_at
        ? new Date(inv.created_at)
        : null;
      let state;
      if (w.session_status === "completed") state = "completed";
      else if (w.session_status === "cancelled") state = "skipped";
      else if (w.session_status === "no-show") state = "noshow";
      else if (w.reason === "removed") state = "removed";
      else if (w.reason === "pack") state = "pack";
      else state = "scheduled";
      return { week: w.week, date, inv, state };
    });
  }
  const invByWeek = new Map();
  (invoices || [])
    .filter((i) => i.recurring_series_id === series.series_id)
    .forEach((i) => {
      if (i.series_week) invByWeek.set(i.series_week, i);
    });
  const anchor = series.next_run_at ? new Date(series.next_run_at) : null;
  const wg = series.weeks_generated;
  const total = series.total_weeks;
  return Array.from({ length: total }, (_, idx) => {
    const week = idx + 1;
    const inv = invByWeek.get(week) || null;
    let date = null;
    if (inv) {
      date = new Date(inv.created_at);
    } else if (anchor && series.status === "active") {
      // week (wg+1) is anchored on `next_run_at`; every other week is ±7 days from there.
      const offsetWeeks = week - (wg + 1);
      date = new Date(anchor.getTime() + offsetWeeks * 7 * 24 * 3600 * 1000);
    }
    let state;
    if (inv) state = "completed";
    else if (week <= wg) state = "skipped";
    else state = "scheduled";
    return { week, date, inv, state };
  });
}

function timelineLabel(entry) {
  switch (entry.state) {
    case "completed":
      return "DONE";
    case "skipped":
      return "SKIPPED";
    case "noshow":
      return "NO-SHOW";
    case "pack":
      return "PACK CREDIT";
    case "removed":
      return "REMOVED";
    case "scheduled":
      return "SCHEDULED";
    default:
      return "";
  }
}

function timelineChipClass(state) {
  const base = "text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded shrink-0";
  if (state === "completed") return `${base} bg-[#0F6B6B]/10 text-[#0F6B6B]`;
  if (state === "noshow") return `${base} bg-[#FBF4DD] text-[#7A5E18]`;
  if (state === "pack") return `${base} bg-[#0F6B6B]/15 text-[#0F6B6B]`;
  if (state === "removed") return `${base} bg-[#EEE9E0] text-[#6B635C]`;
  if (state === "skipped") return `${base} bg-[#EEE9E0] text-[#6B635C]`;
  if (state === "scheduled") return `${base} bg-[#E8E1D6] text-[#2A2623]`;
  return `${base} bg-[#EEE9E0] text-[#6B635C]`;
}

function fmtTimelineDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function RecurringCard({ series, invoices, onReload, credits = 0 }) {
  const [busy, setBusy] = useState(null);
  const [skipAsking, setSkipAsking] = useState(false);
  const [covering, setCovering] = useState(false);
  const isActive = series.status === "active";
  const isFinished = series.status === "completed";
  const total = series.total_weeks;
  const seriesInvoices = (invoices || []).filter(
    (i) => i.recurring_series_id === series.series_id
  );
  const packMode = series.billing_mode === "pack";
  const packWeeks = series.weeks_on_pack ?? 0;
  const completedCount =
    series.weeks_completed ??
    (series.week_states || []).filter((w) => w.session_status === "completed").length;
  const invoicedCount = series.weeks_invoiced ?? seriesInvoices.length;
  const weekLine = `${completedCount} of ${total} classes completed · ${invoicedCount} invoiced${
    packWeeks ? ` · ${packWeeks} on credits` : ""
  }`;
  const timeline = buildTimeline(series, invoices);

  const doSkip = async (who) => {
    setSkipAsking(false);
    setBusy("skip");
    try {
      await api.post(`/recurring/${series.series_id}/skip`, { cancelled_by: who });
      toast.success(`Skipped next week — logged as ${who === "client" ? "client" : "you"} cancelled`);
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const doAction = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "cancel") await api.post(`/recurring/${series.series_id}/cancel`);
      toast.success("Series ended");
      await onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-md border border-[#E8E1D6] bg-[#FFFFFF] p-4 space-y-3"
      data-testid={`recurring-card-${series.series_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-lg font-bold text-[#2A2623] truncate">
            ${series.amount.toFixed(2)} · {WEEKDAY_LONG[series.day_of_week]}s
          </p>
          <p className="text-xs text-[#6B635C] mt-0.5">{series.label || "Weekly training package"}</p>
          <span
            data-testid={`recurring-mode-${series.series_id}`}
            className={`inline-flex mt-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
              packMode
                ? "bg-[#0F6B6B]/15 text-[#0F6B6B]"
                : "bg-[#E1EDF7] text-[#1F5C8F]"
            }`}
          >
            {packMode
              ? `Pack credits${packWeeks ? ` · ${packWeeks} week${packWeeks === 1 ? "" : "s"}` : ""}`
              : "Invoiced per class"}
          </span>
        </div>
        <span className={statusChip(series.status)} data-testid={`recurring-status-${series.series_id}`}>
          {isFinished ? "Finished" : series.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Progress</p>
          <p className="text-[#2A2623] mt-0.5" data-testid={`recurring-progress-${series.series_id}`}>{weekLine}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#6B635C]">Next invoice</p>
          <p className="text-[#2A2623] mt-0.5" data-testid={`recurring-next-${series.series_id}`}>
            {isActive ? "When each class is completed" : "—"}
          </p>
        </div>
      </div>

      <details
        className="rounded border border-[#E8E1D6] bg-[#FAF7F2]/50"
        data-testid={`recurring-timeline-toggle-${series.series_id}`}
      >
        <summary className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#2A2623] font-semibold cursor-pointer px-3 py-2 hover:text-[#2A2623]">
          <CalendarDays className="w-3.5 h-3.5" />
          Preview all {series.total_weeks} dates
          <span className="text-[10px] font-normal text-[#6B635C] normal-case tracking-normal ml-auto">
            spot holidays or clashes upfront
          </span>
        </summary>
        <ol
          className="divide-y divide-[#E8E1D6] border-t border-[#E8E1D6]"
          data-testid={`recurring-timeline-${series.series_id}`}
        >
          {timeline.map((entry) => (
            <li
              key={entry.week}
              className="flex items-center gap-3 px-3 py-2 text-xs"
              data-testid={`recurring-timeline-week-${series.series_id}-${entry.week}`}
            >
              <span className="text-[10px] uppercase tracking-widest text-[#6B635C] w-14 shrink-0 font-semibold">
                Week {entry.week}
              </span>
              <span className="flex-1 text-[#2A2623] min-w-0 truncate">
                {fmtTimelineDate(entry.date)}
                {entry.inv && (
                  <span className="text-[#6B635C] ml-1.5">· {entry.inv.invoice_number}</span>
                )}
              </span>
              <span className={timelineChipClass(entry.state)}>{timelineLabel(entry)}</span>
            </li>
          ))}
        </ol>
      </details>

      {isActive && (
        <div className="flex flex-wrap gap-2 pt-1">
          {credits > 0 && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy !== null}
              onClick={() => setCovering(true)}
              data-testid={`recurring-use-credits-${series.series_id}`}
              className="bg-transparent border-[#0F6B6B]/50 text-[#0F6B6B] hover:bg-[#0F6B6B]/10 text-xs"
            >
              Use pack credits for this series
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => setSkipAsking(true)}
            data-testid={`recurring-skip-${series.series_id}`}
            className="bg-transparent border-[#E8E1D6] text-[#2A2623] hover:bg-[#E8E1D6] text-xs"
          >
            Skip next week
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => doAction("cancel")}
            data-testid={`recurring-cancel-${series.series_id}`}
            className="bg-transparent border-[#F0C9C1] text-[#A8372A] hover:bg-[#F9E4DF] text-xs"
          >
            End series
          </Button>
        </div>
      )}

      {covering && (
        <PackCoverageDialog
          open={covering}
          onOpenChange={setCovering}
          clientId={series.client_id}
          seriesId={series.series_id}
          onDone={onReload}
        />
      )}

      <AlertDialog open={skipAsking} onOpenChange={(o) => !o && setSkipAsking(false)}>
        <AlertDialogContent className="bg-[#FFFFFF] border-[#E8E1D6] text-[#2A2623]" data-testid={`recurring-skip-who-dialog-${series.series_id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl uppercase text-[#2A2623]">Who skipped?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#6B635C]">
              Skips next week's session and its invoice. Logged for your records only — it never changes the reliability score.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid={`recurring-skip-by-client-${series.series_id}`}
              onClick={() => doSkip("client")}
              className="bg-[#FBF4DD] text-[#7A5E18] hover:bg-[#F6ECC9] border border-[#ECDCA6]"
            >
              Client cancelled
            </Button>
            <Button
              data-testid={`recurring-skip-by-trainer-${series.series_id}`}
              onClick={() => doSkip("trainer")}
              className="bg-[#E1EDF7] text-[#1F5C8F] hover:bg-[#D3E4F4] border border-[#B9D4EC]"
            >
              I cancelled
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid={`recurring-skip-dismiss-${series.series_id}`}
              className="border-[#E8E1D6] bg-transparent text-[#2A2623] hover:bg-[#FAF7F2] hover:text-[#2A2623]"
            >
              Keep next week
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function computePreviewDates(dow, total, timeOfDay = "09:00", now = new Date()) {
  // Matches backend `_next_weekday_at`: next occurrence of dow AFTER today
  // at the trainer-chosen HH:MM (never same day; series always starts next
  // week if today matches). Preview only — final dates authoritative once
  // backend returns them.
  const [hh, mm] = String(timeOfDay).split(":").map((v) => parseInt(v, 10) || 0);
  const jsDow = (dow + 1) % 7; // 0=Mon..6=Sun → JS 1=Mon..0=Sun
  const first = new Date(now);
  first.setHours(hh, mm, 0, 0);
  const cur = first.getDay();
  let delta = (jsDow - cur + 7) % 7;
  if (delta === 0) delta = 7;
  first.setDate(first.getDate() + delta);
  return Array.from({ length: total }, (_, i) => {
    const d = new Date(first);
    d.setDate(d.getDate() + i * 7);
    return d;
  });
}

function RecurringLivePreview({ amount, dow, weeks, timeOfDay, durationMin, billingMode, credits }) {
  const amt = Number(amount);
  const wk = Number(weeks);
  const validAmount = amt > 0;
  const validWeeks = Number.isInteger(wk) && wk >= 1 && wk <= 104;
  const validTime = /^\d{2}:\d{2}$/.test(timeOfDay || "");

  if (!validAmount || !validWeeks || !validTime) {
    return (
      <div
        data-testid="recurring-create-preview-empty"
        className="rounded-md border border-dashed border-[#E8E1D6] bg-[#FAF7F2]/60 p-3 text-xs text-[#6B635C]"
      >
        Enter a valid rate, time and number of weeks to preview every class.
      </div>
    );
  }

  const dates = computePreviewDates(dow, wk, timeOfDay);
  const packMode = billingMode === "pack" && credits > 0;
  const packWeeks = packMode ? Math.min(credits, wk) : 0;
  const invoiceWeeks = wk - packWeeks;
  const totalCommitted = amt * invoiceWeeks;
  const firstInvoiceWeek = packWeeks + 1;

  return (
    <div
      data-testid="recurring-create-preview"
      className="rounded-md border border-[#E8E1D6] bg-[#FAF7F2]/60"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E8E1D6]">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-3.5 h-3.5 text-[#0F6B6B]" />
          <span className="text-[11px] uppercase tracking-widest font-semibold text-[#2A2623]">
            Preview · every date
          </span>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest text-[#6B635C]"
          data-testid="recurring-create-preview-total"
        >
          {packMode
            ? `${packWeeks} on credits · ${invoiceWeeks} invoiced (${fmtAudCompact(totalCommitted)})`
            : `Total ${fmtAudCompact(totalCommitted)} across ${wk} weeks`}
        </span>
      </div>
      {packMode && (
        <p
          className="text-[11px] text-[#0F6B6B] bg-[#0F6B6B]/5 border-b border-[#E8E1D6] px-3 py-1.5"
          data-testid="recurring-create-split"
        >
          {invoiceWeeks === 0
            ? `All ${packWeeks} weeks come out of the ${credits} prepaid credit${credits === 1 ? "" : "s"} — no invoices.`
            : `Weeks 1–${packWeeks} use the ${credits} prepaid credit${credits === 1 ? "" : "s"}; weeks ${firstInvoiceWeek}–${wk} are invoiced at ${fmtAudCompact(amt)} each when completed.`}
        </p>
      )}
      <p
        className="text-[11px] text-[#2A2623] px-3 py-1.5 border-b border-[#E8E1D6]"
        data-testid="recurring-create-summary"
      >
        {WEEKDAY_FULL[dow]}s at {fmtTimeWords(timeOfDay)}, {Number(durationMin) || 60} min, {wk} week{wk === 1 ? "" : "s"}
      </p>
      <ol
        className="divide-y divide-[#E8E1D6]"
        data-testid="recurring-create-preview-list"
      >
        {dates.map((d, idx) => (
          <li
            key={idx}
            className="flex items-center gap-3 px-3 py-1.5 text-xs"
            data-testid={`recurring-create-preview-week-${idx + 1}`}
          >
            <span className="text-[10px] uppercase tracking-widest text-[#6B635C] w-14 shrink-0 font-semibold">
              Week {idx + 1}
            </span>
            <span className="flex-1 text-[#2A2623] truncate">
              {fmtTimelineDate(d)} · {fmtTimeWords(timeOfDay)}
            </span>
            {idx < packWeeks ? (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#0F6B6B]/15 text-[#0F6B6B]">
                PACK CREDIT
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#E8E1D6] text-[#2A2623]">
                SCHEDULED
              </span>
            )}
          </li>
        ))}
      </ol>
      <p className="text-[10px] text-[#6B635C] px-3 py-1.5 border-t border-[#E8E1D6]">
        Check for public holidays or clashes now. You can Skip any week after creating.
      </p>
    </div>
  );
}

function fmtAudCompact(n) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(n);
}

function RecurringCreateDialog({ open, onOpenChange, client, credits = 0, onCreated }) {
  const [amount, setAmount] = useState("90");
  const [dow, setDow] = useState(0);
  const [weeks, setWeeks] = useState("8");
  const [timeOfDay, setTimeOfDay] = useState("09:00");
  const [durationMin, setDurationMin] = useState("60");
  const [sessionType, setSessionType] = useState("in-person");
  const [billingMode, setBillingMode] = useState(credits > 0 ? "pack" : "invoice");
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);

  useEffect(() => {
    if (open) {
      setAmount("90");
      setDow(0);
      setWeeks("8");
      setTimeOfDay("09:00");
      setDurationMin("60");
      setSessionType("in-person");
      setBillingMode(credits > 0 ? "pack" : "invoice");
      setConflicts(null);
    }
  }, [open, credits]);

  // Clear any lingering conflict list whenever the trainer edits the schedule
  useEffect(() => {
    setConflicts(null);
  }, [dow, timeOfDay, durationMin, weeks]);

  const hasConflicts =
    !!conflicts &&
    Array.isArray(conflicts.conflicts) &&
    conflicts.conflicts.length > 0;

  const submit = async () => {
    const amt = Number(amount);
    const wk = Number(weeks);
    const dur = Number(durationMin);
    if (!amt || amt <= 0) return toast.error("Enter a valid rate");
    if (!wk || wk < 1 || wk > 104) return toast.error("Weeks must be between 1 and 104");
    if (!dur || dur <= 0 || dur > 480) return toast.error("Duration must be 1..480 min");
    if (!/^\d{2}:\d{2}$/.test(timeOfDay)) return toast.error("Time must be HH:MM");
    if (isOutsideWindow(timeOfDay, dur)) return toast.error(BOOKING_WINDOW.message);
    setSaving(true);
    setConflicts(null);
    try {
      await api.post(`/clients/${client.client_id}/recurring`, {
        client_id: client.client_id,
        amount: amt,
        day_of_week: dow,
        total_weeks: wk,
        time_of_day: timeOfDay,
        duration_min: dur,
        session_type: sessionType,
        billing_mode: credits > 0 ? billingMode : "invoice",
      });
      toast.success(
        credits > 0 && billingMode === "pack"
          ? "Recurring series created. Pack credits cover the first weeks; every other class is invoiced when you mark it complete."
          : "Recurring series created. All classes booked — each one is invoiced when you mark it complete."
      );
      onOpenChange(false);
      await onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.conflicts)) {
        setConflicts(detail);
        toast.error(detail.message || "Series clashes with existing bookings");
      } else {
        toast.error(detail || "Could not create series");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="recurring-create-dialog"
        className="bg-[#FAF7F2] border-[#E8E1D6] text-[#2A2623] max-w-md w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] p-0 gap-0 flex flex-col overflow-hidden"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-[#E8E1D6]">
          <DialogTitle className="font-display uppercase">New recurring series</DialogTitle>
          <p className="text-[11px] text-[#6B635C] mt-1" data-testid="recurring-create-explainer">
            Classes are booked now. Each one is invoiced when you mark it complete.
          </p>
        </DialogHeader>
        <div className="space-y-4 px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {credits > 0 && (
            <div data-testid="recurring-billing-mode">
              <label className="tm-label">
                {client.name} has {credits} prepaid credit{credits === 1 ? "" : "s"}
              </label>
              <div className="grid grid-cols-2 gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setBillingMode("pack")}
                  aria-pressed={billingMode === "pack"}
                  data-testid="recurring-mode-pack"
                  className={`py-2 px-2 text-[11px] uppercase tracking-widest font-bold rounded transition-colors ${
                    billingMode === "pack"
                      ? "bg-[#0F6B6B] text-[#FFFFFF]"
                      : "bg-[#FFFFFF] text-[#2A2623] hover:bg-[#E8E1D6]"
                  }`}
                >
                  Use pack credits
                </button>
                <button
                  type="button"
                  onClick={() => setBillingMode("invoice")}
                  aria-pressed={billingMode === "invoice"}
                  data-testid="recurring-mode-invoice"
                  className={`py-2 px-2 text-[11px] uppercase tracking-widest font-bold rounded transition-colors ${
                    billingMode === "invoice"
                      ? "bg-[#0F6B6B] text-[#FFFFFF]"
                      : "bg-[#FFFFFF] text-[#2A2623] hover:bg-[#E8E1D6]"
                  }`}
                >
                  Invoice after each class
                </button>
              </div>
              <p className="text-[11px] text-[#6B635C] mt-1">
                {billingMode === "pack"
                  ? "Credits are spent week by week — those weeks are never invoiced."
                  : "Each class is invoiced when you mark it complete; the prepaid credits stay untouched."}
              </p>
            </div>
          )}
          <div>
            <label className="tm-label" htmlFor="rec-amount">Rate per session (AUD, GST-incl.)</label>
            <Input
              id="rec-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="recurring-amount"
              className="bg-[#FFFFFF] border-[#E8E1D6] mt-1"
            />
          </div>
          <div>
            <label className="tm-label">Day of week</label>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {WEEKDAY_LABELS.map((lbl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setDow(i)}
                  data-testid={`recurring-dow-${i}`}
                  className={`py-2 text-xs uppercase tracking-widest font-bold rounded transition-colors ${
                    dow === i
                      ? "bg-[#0F6B6B] text-[#FFFFFF]"
                      : "bg-[#FFFFFF] text-[#2A2623] hover:bg-[#E8E1D6]"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <TimeGrid
            testid="recurring-time"
            value={timeOfDay}
            durationMin={durationMin}
            onChange={setTimeOfDay}
            label="Class start time"
          />
          <div className="grid grid-cols-1 gap-3">
            <div className="min-w-0">
              <label className="tm-label" htmlFor="rec-duration">Duration (min)</label>
              <select
                id="rec-duration"
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                data-testid="recurring-duration"
                className="w-full min-w-0 mt-1 bg-[#FFFFFF] border border-[#E8E1D6] rounded-md px-2 text-[#2A2623] text-sm appearance-none"
                style={{
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                  appearance: "none",
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  height: "40px",
                }}
              >
                {[30, 45, 60, 75, 90, 120].map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="tm-label">Session type</label>
            <div className="grid grid-cols-3 gap-1 mt-1">
              {["in-person", "online", "outdoor"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setSessionType(t)}
                  data-testid={`recurring-type-${t}`}
                  className={`py-2 text-xs uppercase tracking-widest font-bold rounded transition-colors ${
                    sessionType === t
                      ? "bg-[#0F6B6B] text-[#FFFFFF]"
                      : "bg-[#FFFFFF] text-[#2A2623] hover:bg-[#E8E1D6]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="tm-label" htmlFor="rec-weeks">Number of weeks</label>
            <Input
              id="rec-weeks"
              type="number"
              min="1"
              max="104"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
              data-testid="recurring-weeks"
              className="bg-[#FFFFFF] border-[#E8E1D6] mt-1"
            />
            <p className="text-[11px] text-[#6B635C] mt-1">
              Books this many classes. Each one is invoiced when you mark it complete.
            </p>
          </div>

          <RecurringLivePreview
            amount={amount}
            dow={dow}
            weeks={weeks}
            timeOfDay={timeOfDay}
            durationMin={durationMin}
            billingMode={credits > 0 ? billingMode : "invoice"}
            credits={credits}
          />

          {conflicts && Array.isArray(conflicts.conflicts) && conflicts.conflicts.length > 0 && (
            <div
              className="rounded-md border border-[#E7B9B2] bg-[#F9E4DF] p-3 space-y-2"
              data-testid="recurring-conflict-panel"
            >
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#A8372A]">
                {conflicts.conflicts.length} week{conflicts.conflicts.length === 1 ? "" : "s"} clash
              </div>
              <p className="text-xs text-[#A8372A] leading-relaxed">
                {conflicts.message || "Change the day or time — nothing will be booked until every week is free."}
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                {conflicts.conflicts.map((c) => (
                  <li
                    key={c.week_index}
                    className="flex items-center gap-2 text-[11px] text-[#A8372A]"
                    data-testid={`recurring-conflict-row-${c.week_index}`}
                  >
                    <span className="text-[10px] uppercase tracking-widest text-[#A8372A]/70 w-14 shrink-0 font-semibold">
                      Week {c.week_index}
                    </span>
                    <span className="flex-1 truncate">
                      {c.date_mel} · {c.time_mel} — clashes with{" "}
                      <span className="font-semibold">
                        {c.clash_with?.client_name || "another booking"}
                      </span>
                      {c.clash_with?.time_mel && c.clash_with.time_mel !== c.time_mel
                        ? ` at ${c.clash_with.time_mel}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-4 shrink-0 border-t border-[#E8E1D6] bg-[#FAF7F2]">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="bg-transparent border-[#E8E1D6] text-[#2A2623] hover:bg-[#E8E1D6]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || hasConflicts}
            data-testid="recurring-create-submit"
            title={hasConflicts ? "Change the day or time — every week must be free." : undefined}
            className="bg-[#0F6B6B] text-[#FFFFFF] hover:bg-[#0B5757] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : hasConflicts ? "Fix clashes to create" : "Create series"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}